import { b2x } from '@b2x/react/src';
import React from 'react';

// Mantengo planetpay per retrocompatibilità
export type Brand = 'lottomatica' | 'goldbet' | 'planetwin365' | 'lottomaticaservizi';
export type ShopBrand = Brand | 'intralot' | 'planetpay365' | 'better';
export type BrandHostname =
  | 'goldbet.it'
  | 'lottomatica.it'
  | 'lottomaticaservizi.com'
  | 'planetpay365.it'
  | 'planetwin365.it';
export const hostnameToBrandMap = {
  'goldbet.it': 'goldbet',
  'lottomatica.it': 'lottomatica',
  'lottomaticaservizi.com': 'lottomaticaservizi',
  'planetpay365.it': 'lottomaticaservizi',
  'planetwin365.it': 'planetwin365',
} as const satisfies Record<BrandHostname, Brand | undefined>;

interface AppContextInterface {
  brand: Brand;
  getBrandVariant<T>(map: Record<Brand, T>): T;
  mock: boolean;
  reverseProxy: boolean;
}

export const [AppContextProvider, useAppContext] = b2x.createContext<AppContextInterface>('AppContext');

interface AppStaticContextInterface {
  setMock: React.Dispatch<React.SetStateAction<boolean>>;
}

export const [AppStaticContextProvider, useAppStaticContext] =
  b2x.createContext<AppStaticContextInterface>('AppStaticContext');

interface UseAppContextInitializerProps {
  brand: Brand;
  reverseProxy: boolean;
}

const useAppContextInitializer = ({ brand, reverseProxy }: UseAppContextInitializerProps) => {
  const [mock, setMock] = React.useState<boolean>(false);

  const getBrandVariant = React.useCallback(
    <T,>(map: Record<Brand, T>) => {
      return map[brand];
    },
    [brand]
  );

  const appContext: AppContextInterface = React.useMemo(
    () => ({ brand, getBrandVariant, mock, reverseProxy }),
    [getBrandVariant, mock, reverseProxy, brand]
  );

  const appStaticContext: AppStaticContextInterface = React.useMemo(() => ({ setMock }), []);

  return {
    AppContextProvider,
    AppStaticContextProvider,
    appContext,
    appStaticContext,
  };
};

export interface AppContextProps extends UseAppContextInitializerProps {
  children:
    | React.ReactNode
    | ((appContext: AppContextInterface, appStaticContext: AppStaticContextInterface) => React.ReactNode);
}

export const AppContext = ({ children, ...otherProps }: AppContextProps) => {
  const appContextInitializer = useAppContextInitializer(otherProps);

  React.useEffect(() => {
    document.documentElement.id = appContextInitializer.appContext.brand;
  }, [appContextInitializer.appContext.brand]);

  return (
    <appContextInitializer.AppContextProvider value={appContextInitializer.appContext}>
      <appContextInitializer.AppStaticContextProvider value={appContextInitializer.appStaticContext}>
        {typeof children === 'function'
          ? children(appContextInitializer.appContext, appContextInitializer.appStaticContext)
          : children}
      </appContextInitializer.AppStaticContextProvider>
    </appContextInitializer.AppContextProvider>
  );
};
